import * as React from "react";

import { Alert, Button, Dialog, DialogTitle, DialogContent, TableCell } from "@mui/material";
import { styled } from "@mui/material/styles";
import { FilterType, MUIDataTableColumn } from "mui-datatables";

import RangeFilter from "src/common/components/filters/RangeFilter";
import { DiskTable } from "src/common/components/table/DiskTable";
import { TableLoader } from "src/common/components/table/TableLoader";
import useBridgeSWR from "src/common/hooks/BridgeSWRClient";
import { useDeleteDisk } from "src/domain/core/disks/api/deleteDisk";
import EditDiskForm from "src/domain/core/disks/components/EditDiskForm";
import { endpoint } from "src/lib/bridgeClient";
import { filterLogic, filterChip } from "src/lib/filters/rangeFilter";
import { isDeleteAllowed } from "src/lib/table/disks";
import theme from "theme";

function isEditAllowed(diskStatus: string): boolean {
  return diskStatus == "available";
}

const noMatchState = (error, disks) => {
  if (error) {
    return "Unable to load disks";
  }

  if (!disks) {
    return <TableLoader message={"Loading your disks..."} />;
  }

  return "No disks found";
};

const resourceIdIndex = 1; // Used for row selection to be based on resource ID
const columns: MUIDataTableColumn[] = [
  {
    name: "diskName",
    label: "Disk Name",
    options: {
      filter: true,
      sort: true,
      filterType: "textField" as FilterType,
    },
  },
  {
    name: "diskVolumeId",
    label: "Volume ID",
    options: {
      filter: false,
      sort: true,
    },
  },
  {
    name: "diskStatus",
    label: "Status",
    options: {
      filter: true,
      sort: true,
      customBodyRender: (value) => {
        const StatusStyle = styled("p")(() => ({
          color: isDeleteAllowed(value) && theme?.palette?.warning?.main,
        }));
        return <StatusStyle>{value}</StatusStyle>;
      },
    },
  },
  {
    name: "diskSize",
    label: "Size (GB)",
    options: {
      filter: true,
      sort: true,
      filterType: "custom" as FilterType,
      filterOptions: {
        logic: (size, filters) => {
          return filterLogic(size, filters);
        },
        display: (filterList, onChange, index, column) => (
          <RangeFilter heading="Size" filterList={filterList} onChange={onChange} index={index} column={column} />
        ),
      },
      customFilterListOptions: {
        render: (v) => {
          return filterChip(v, "Size");
        },
      },
    },
  },
  {
    name: "editButton",
    options: {
      viewColumns: false,
      filter: false,
      sort: false,
      customHeadRender: () => {
        return <TableCell key="edit" />; // Edit column doesn't need a heading label
      },
    },
  },
];

const transform = (disks: Array<Disk>) => {
  const diskRows = disks.map((disk: Disk) => {
    const diskRow = {
      id: disk.id, // Needed for our checkbox-error workaround.
      diskVolumeId: disk.id,
      diskName: disk.name,
      diskSize: disk.size,
      diskStatus: disk.status,
      editButton: <EditButton disk={disk} />,
    };

    return diskRow;
  });

  return diskRows;
};

export const UserDiskTable = ({ refreshInterval }: { refreshInterval: number }) => {
  const deleteDisk = useDeleteDisk();

  const { data, error } = useBridgeSWR<Disk[]>(endpoint.getMyDisks, {
    refreshInterval: refreshInterval,
  });

  const disks = React.useMemo(() => {
    if (data) {
      return transform(data);
    }
  }, [data]);

  return (
    <DiskTable
      disks={disks}
      resourceIdIndex={resourceIdIndex}
      noMatchState={noMatchState(error, disks)}
      columns={columns}
      deleteDisk={deleteDisk}
    />
  );
};

interface EditButtonProps {
  disk: Disk;
}

const EditButton = ({ disk }: EditButtonProps) => {
  const [open, setOpen] = React.useState(false);

  return (
    <>
      <Button data-testid={disk.name + "-edit"} onClick={() => setOpen(true)} disabled={!isEditAllowed(disk.status)}>
        Edit
      </Button>
      <EditDiskDialog open={open} close={() => setOpen(false)} disk={disk} />
    </>
  );
};

interface EditDiskDialogProps {
  open: boolean;
  close: () => void;
  disk: Disk;
}

const EditDiskDialog = ({ open, close, disk }: EditDiskDialogProps) => {
  return (
    <>
      <Dialog open={open} onClose={close}>
        <DialogTitle>
          Edit disk <b>{disk?.name}</b>
        </DialogTitle>
        <DialogContent sx={{ mt: 5 }}>
          <Alert severity="warning" sx={{ marginBottom: "15px" }}>
            Disk size may only be increased once every 6 hours
          </Alert>
          <EditDiskForm disk={disk} close={close} />
        </DialogContent>
      </Dialog>
    </>
  );
};
